import { useState } from "react";
import api, { formatApiError } from "@/lib/api";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Mail, MessageCircle, Send, Loader2, CheckCircle2, Instagram } from "lucide-react";

export default function Contato() {
    const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
    const [state, setState] = useState({ sending: false, sent: false, error: "" });

    const send = async () => {
        setState({ sending: true, sent: false, error: "" });
        try {
            await api.post("/contact", form);
            setState({ sending: false, sent: true, error: "" });
            setForm({ name: "", email: "", subject: "", message: "" });
        } catch (e) {
            setState({ sending: false, sent: false, error: formatApiError(e) });
        }
    };

    return (
        <div className="min-h-screen relative grain">
            <Header />
            <main className="max-w-[1100px] mx-auto px-6 md:px-10 pt-14 pb-24">
                <div className="overline text-[#5C5C5C]">Contato</div>
                <h1 className="font-display font-light text-5xl md:text-6xl mt-2 tracking-tight">
                    Vamos <span className="font-editorial italic text-holo">conversar</span>
                </h1>
                <p className="font-body text-[#5C5C5C] mt-4 max-w-2xl">
                    Estamos aqui para tirar suas dúvidas, apoiar sua jornada e ouvir o que você precisa.
                </p>

                <div className="mt-12 grid md:grid-cols-[1.2fr_1fr] gap-8">
                    {/* Form */}
                    <div className="bg-white rounded-3xl p-8 md:p-10 border border-black/5">
                        <h2 className="font-display text-2xl tracking-tight">Envie uma mensagem</h2>
                        {state.sent ? (
                            <div data-testid="contact-success" className="mt-8 text-center py-10">
                                <div className="w-14 h-14 rounded-full bg-[#00D4D4]/15 border border-[#00D4D4]/30 flex items-center justify-center mx-auto">
                                    <CheckCircle2 className="w-7 h-7 text-[#00D4D4]" />
                                </div>
                                <div className="font-display text-2xl mt-5 tracking-tight">Mensagem enviada</div>
                                <p className="font-body text-sm text-[#5C5C5C] mt-2">Nossa equipe responde em até 48 horas úteis.</p>
                                <button onClick={() => setState({ sending: false, sent: false, error: "" })} className="btn-ghost mt-6">Enviar outra</button>
                            </div>
                        ) : (
                            <div className="mt-6 space-y-4">
                                <div className="grid sm:grid-cols-2 gap-4">
                                    <div>
                                        <label className="overline text-[#5C5C5C]">Nome</label>
                                        <input data-testid="contact-name" className="imq-input mt-2" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
                                    </div>
                                    <div>
                                        <label className="overline text-[#5C5C5C]">E-mail</label>
                                        <input data-testid="contact-email" type="email" className="imq-input mt-2" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
                                    </div>
                                </div>
                                <div>
                                    <label className="overline text-[#5C5C5C]">Assunto</label>
                                    <input data-testid="contact-subject" className="imq-input mt-2" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} />
                                </div>
                                <div>
                                    <label className="overline text-[#5C5C5C]">Mensagem</label>
                                    <textarea data-testid="contact-message" rows={5} className="imq-input mt-2" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} />
                                </div>
                                {state.error && <div data-testid="contact-error" className="text-sm text-red-600 bg-red-50 border border-red-100 px-3 py-2 rounded-lg">{state.error}</div>}
                                <button
                                    data-testid="contact-submit"
                                    onClick={send}
                                    disabled={state.sending || !form.name || !form.email || !form.message}
                                    className="btn-primary justify-center w-full mt-2"
                                >
                                    {state.sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                                    {state.sending ? "Enviando..." : "Enviar mensagem"}
                                </button>
                            </div>
                        )}
                    </div>

                    {/* Channels */}
                    <aside className="space-y-4">
                        <div className="bg-white rounded-3xl p-7 border border-black/5">
                            <Mail className="w-5 h-5 text-[#1A1A1A]" />
                            <div className="overline text-[#5C5C5C] mt-3">E-mail</div>
                            <a href="mailto:contato@medicinaquantica.com" className="font-display text-lg mt-1 block hover:text-holo transition-colors">contato@medicinaquantica.com</a>
                        </div>
                        <div className="bg-white rounded-3xl p-7 border border-black/5">
                            <MessageCircle className="w-5 h-5 text-[#1A1A1A]" />
                            <div className="overline text-[#5C5C5C] mt-3">Suporte ao aluno</div>
                            <a href="https://wa.me/5511999999999" target="_blank" rel="noreferrer" className="font-display text-lg mt-1 block hover:text-holo transition-colors">WhatsApp</a>
                            <div className="font-body text-xs text-[#5C5C5C] mt-1">Resposta em até 24h úteis</div>
                        </div>
                        <div className="bg-white rounded-3xl p-7 border border-black/5">
                            <Instagram className="w-5 h-5 text-[#1A1A1A]" />
                            <div className="overline text-[#5C5C5C] mt-3">Redes</div>
                            <a href="https://instagram.com" target="_blank" rel="noreferrer" className="font-display text-lg mt-1 block hover:text-holo transition-colors">@medicinaquantica</a>
                        </div>
                    </aside>
                </div>
            </main>
            <Footer />
        </div>
    );
}
